import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  LifeBuoy, Settings, Send, Search, ArrowLeft, Smartphone, AlertTriangle, CheckCircle2, Wrench, ExternalLink, Bot, Link2, Unplug, MessageCircle,
} from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, Card, CardBody, Button, Field, Input, Textarea, Select, Badge, Avatar, Modal, Tabs, EmptyState, LoadingBlock } from '../../ui';

const STATUS_TABS = ['open', 'new', 'in_progress', 'resolved', 'all'];
const STATUS_TONE = { new: 'brand', in_progress: 'warning', resolved: 'success' };
const REFRESH_MS = 20000;
const nameOf = (r) => [r.first_name, r.last_name].filter(Boolean).join(' ').trim();

/** Техпідтримка: заявки співробітників усіх компаній (бачить лише власник платформи) */
export default function SupportPage() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [status, setStatus] = useState('open');
  const [tenantId, setTenantId] = useState('');
  const [search, setSearch] = useState('');
  const [companies, setCompanies] = useState([]);
  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const selected = Number(params.get('ticket')) || null;

  const loadSettings = useCallback(() => {
    api.get('/admin/support/settings').then((res) => setSettings(res.data.data)).catch(() => setSettings({ configured: false }));
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => {
    api.get('/admin/tenants').then((res) => setCompanies(res.data.data || [])).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    api.get('/admin/support/tickets', { params: { status, ...(tenantId ? { tenant_id: tenantId } : {}), ...(search.trim() ? { search: search.trim() } : {}) } })
      .then((res) => { setList(res.data.data || []); setCounts(res.data.counts || {}); })
      .catch((err) => { setList([]); toast.error(errorText(err, t('common.error'))); });
  }, [status, tenantId, search, t]);
  useEffect(() => {
    const id = setTimeout(loadList, search ? 300 : 0);
    const timer = setInterval(loadList, REFRESH_MS);
    return () => { clearTimeout(id); clearInterval(timer); };
  }, [loadList, search]);

  const select = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('ticket', String(id)); else next.delete('ticket');
    setParams(next, { replace: true });
  };

  const openCount = (counts.new || 0) + (counts.in_progress || 0);

  return (
    <>
      <PageHeader
        title={t('support.title')}
        description={t('support.subtitle')}
        actions={settings?.configured && <Button variant="secondary" icon={Settings} onClick={() => setSettingsOpen(true)}>{t('support.botSettings')}</Button>}
      />

      {!settings ? <LoadingBlock /> : !settings.configured ? (
        <BotSetup onSaved={(data) => setSettings(data)} />
      ) : (
        <>
          {!settings.owner_connected && <OwnerConnectBanner settings={settings} onRefresh={loadSettings} />}

          <div className="grid gap-5 lg:grid-cols-[minmax(320px,400px)_1fr]">
            <Card className={clsx(selected && 'hidden lg:block')}>
              <CardBody className="space-y-3">
                <Tabs
                  value={status}
                  onChange={setStatus}
                  items={STATUS_TABS.map((s) => ({ id: s, label: t(`support.tabs.${s}`), count: s === 'open' ? openCount : s === 'all' ? undefined : counts[s] || 0 }))}
                />
                <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
                  <Input icon={Search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('support.searchPlaceholder')} aria-label={t('common.search')} />
                  <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)} aria-label={t('support.company')}>
                    <option value="">{t('support.allCompanies')}</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </div>
                {!list ? <LoadingBlock /> : !list.length ? (
                  <EmptyState icon={LifeBuoy} title={t('support.empty')} text={t('support.emptyHint')} />
                ) : (
                  <ul className="-mx-2 max-h-[65vh] space-y-1 overflow-y-auto custom-scrollbar">
                    {list.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => select(r.id)}
                          className={clsx('flex w-full items-start gap-3 rounded-xl px-2 py-2.5 text-left transition-colors',
                            selected === r.id ? 'bg-brand-50 dark:bg-brand-950/40' : 'hover:bg-zinc-50 dark:hover:bg-zinc-900')}
                        >
                          <Avatar name={nameOf(r) || r.phone} src={r.avatar_url || undefined} size="md" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{nameOf(r) || `+${r.phone}`}</p>
                              <span className="tabular shrink-0 text-xs text-zinc-400">#{r.id}</span>
                            </div>
                            <p className="truncate text-xs text-zinc-500">{r.tenant_name}</p>
                            <p className={clsx('mt-0.5 truncate text-sm', r.last_from === 'user' && r.status !== 'resolved' ? 'font-medium text-zinc-900 dark:text-zinc-100' : 'text-zinc-500')}>
                              {r.last_from === 'support' ? `${t('support.you')}: ` : ''}{r.last_text}
                            </p>
                          </div>
                          <Badge tone={STATUS_TONE[r.status]} className="shrink-0">{t(`support.status.${r.status}`)}</Badge>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <div className={clsx(!selected && 'hidden lg:block')}>
              {selected ? (
                <TicketView key={selected} id={selected} onBack={() => select(null)} onChanged={loadList} />
              ) : (
                <Card><EmptyState icon={MessageCircle} title={t('support.pick')} text={t('support.pickHint')} /></Card>
              )}
            </div>
          </div>
        </>
      )}

      <BotSettingsModal open={settingsOpen} settings={settings} onClose={() => setSettingsOpen(false)} onChanged={(data) => setSettings(data)} />
    </>
  );
}

function TicketView({ id, onBack, onChanged }) {
  const { t, fmt } = useI18n();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);

  const load = useCallback(() => {
    api.get(`/admin/support/tickets/${id}`).then((res) => setData(res.data.data)).catch((err) => {
      setData(null);
      toast.error(errorText(err, t('common.error')));
    });
  }, [id, t]);
  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);
  const messageCount = data?.messages.length;
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messageCount]);

  if (!data) return <Card><LoadingBlock /></Card>;
  const { ticket, messages, errors } = data;
  const name = nameOf(ticket);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/support/tickets/${id}/reply`, { text: reply.trim() });
      if (!res.data.delivered) toast(t('support.notDelivered'), { icon: '⚠️' });
      else toast.success(t('support.sent'));
      setReply('');
      load();
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${id}/status`, { status });
      load();
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const device = [ticket.app_version, ticket.device_model, ticket.os_version ? `iOS ${ticket.os_version}` : null].filter(Boolean).join(' · ');

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack} className="lg:hidden" aria-label={t('common.back')} />
            <Avatar name={name || ticket.phone} src={ticket.avatar_url || undefined} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {name || `+${ticket.phone}`} <span className="font-normal text-zinc-400">· #{ticket.id}</span>
              </p>
              <p className="truncate text-sm text-zinc-500">
                {ticket.tenant_name} · +{ticket.phone}{ticket.tg_username ? ` · @${ticket.tg_username}` : ''}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[ticket.status]}>{t(`support.status.${ticket.status}`)}</Badge>
            {ticket.status !== 'in_progress' && <Button size="sm" variant="secondary" icon={Wrench} onClick={() => setStatus('in_progress')} disabled={busy}>{t('support.takeInWork')}</Button>}
            {ticket.status !== 'resolved' && <Button size="sm" variant="secondary" icon={CheckCircle2} onClick={() => setStatus('resolved')} disabled={busy}>{t('support.resolve')}</Button>}
          </div>
        </div>

        <div className="grid gap-2 rounded-xl bg-zinc-50 p-3 text-sm dark:bg-zinc-900 sm:grid-cols-2">
          <p className="flex items-center gap-2 text-zinc-600 dark:text-zinc-400"><Smartphone className="h-4 w-4 shrink-0" />{device || t('support.noDevice')}</p>
          <p className="text-zinc-600 dark:text-zinc-400">{t('support.created')}: <span className="tabular">{fmt.dateTime(ticket.created_at)}</span></p>
          {ticket.context && <p className="sm:col-span-2 text-zinc-700 dark:text-zinc-300">🧭 {ticket.context}</p>}
          {errors.length > 0 && (
            <details className="sm:col-span-2">
              <summary className="flex cursor-pointer items-center gap-2 text-amber-700 dark:text-amber-300"><AlertTriangle className="h-4 w-4" />{t('support.appErrors', { count: errors.length })}</summary>
              <ul className="mt-2 space-y-1 text-xs text-zinc-600 dark:text-zinc-400">
                {errors.map((e, i) => <li key={i}><span className="tabular text-zinc-400">{fmt.dateTime(e.timestamp)}</span> · {e.message}</li>)}
              </ul>
            </details>
          )}
        </div>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1 custom-scrollbar">
          {messages.map((m) => (
            <div key={m.id} className={clsx('flex', m.author === 'support' ? 'justify-end' : 'justify-start')}>
              <div className={clsx('max-w-[85%] rounded-2xl px-3.5 py-2 text-sm',
                m.author === 'support' ? 'bg-brand-600 text-white' : 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100')}>
                {m.has_photo ? <SupportPhoto messageId={m.id} /> : null}
                {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                <p className={clsx('mt-1 text-[11px]', m.author === 'support' ? 'text-white/70' : 'text-zinc-400')}>
                  {m.author === 'support' ? `${m.admin_name || 'Telegram'} · ` : ''}{fmt.dateTime(m.created_at)}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottom} />
        </div>

        <div className="space-y-2 border-t border-zinc-200 pt-3 dark:border-zinc-800">
          <Textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder={t('support.replyPlaceholder')}
            maxLength={3500}
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-zinc-400">{t('support.replyHint')}</p>
            <Button icon={Send} onClick={send} loading={busy} disabled={!reply.trim()}>{t('support.send')}</Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** Фото зі звернення: завантажуємо з авторизацією і показуємо */
function SupportPhoto({ messageId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/admin/support/messages/${messageId}/photo`, { responseType: 'blob' })
      .then((res) => { url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => setSrc(''));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [messageId]);
  if (src === null) return <div className="mb-1 h-40 w-56 animate-pulse rounded-lg bg-black/10" />;
  if (!src) return <p className="mb-1 text-xs opacity-70">📷</p>;
  return <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className="mb-1 max-h-72 rounded-lg object-contain" /></a>;
}

/** Бот ще не підключено: інструкція і токен */
function BotSetup({ onSaved }) {
  const { t } = useI18n();
  return (
    <Card>
      <CardBody className="space-y-5">
        <div className="flex items-start gap-3">
          <Bot className="mt-0.5 h-6 w-6 shrink-0 text-brand-600" />
          <div>
            <p className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{t('support.setup.title')}</p>
            <p className="mt-1 max-w-2xl text-sm text-zinc-500">{t('support.setup.text')}</p>
          </div>
        </div>
        <ol className="max-w-2xl list-decimal space-y-1.5 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
          {[1, 2, 3, 4].map((n) => <li key={n}>{t(`support.setup.step${n}`)}</li>)}
        </ol>
        <TokenForm onSaved={onSaved} />
      </CardBody>
    </Card>
  );
}

function TokenForm({ onSaved, compact }) {
  const { t } = useI18n();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.put('/admin/support/settings', { token: token.trim() });
      toast.success(t('support.setup.connected', { name: res.data.data.username }));
      setToken('');
      onSaved(res.data.data);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className={clsx('flex flex-col gap-2 sm:flex-row sm:items-end', !compact && 'max-w-2xl')}>
      <Field label={t('support.setup.token')} className="flex-1">
        <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="1234567890:AA…" autoComplete="off" spellCheck={false} />
      </Field>
      <Button type="submit" icon={Link2} loading={busy} disabled={token.trim().length < 20}>{t('support.setup.connect')}</Button>
    </form>
  );
}

function OwnerConnectBanner({ settings, onRefresh }) {
  const { t } = useI18n();
  return (
    <Card className="mb-5 border-amber-300 dark:border-amber-800">
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('support.owner.title')}</p>
            <p className="mt-0.5 max-w-2xl text-xs text-zinc-500">{t('support.owner.text')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {settings.owner_link && (
            <a href={settings.owner_link} target="_blank" rel="noreferrer"><Button icon={ExternalLink}>{t('support.owner.connect')}</Button></a>
          )}
          <Button variant="secondary" onClick={onRefresh}>{t('support.owner.check')}</Button>
        </div>
      </CardBody>
    </Card>
  );
}

function BotSettingsModal({ open, settings, onClose, onChanged }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!settings?.configured) return null;
  const disconnect = async () => {
    if (!window.confirm(t('support.settings.disconnectConfirm'))) return;
    setBusy(true);
    try {
      const res = await api.delete('/admin/support/settings');
      onChanged(res.data.data);
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title={t('support.botSettings')}>
      <div className="space-y-4 text-sm">
        <p className="text-zinc-700 dark:text-zinc-300">
          {t('support.settings.bot')}: <a className="font-semibold text-brand-600" href={`https://t.me/${settings.username}`} target="_blank" rel="noreferrer">@{settings.username}</a>
        </p>
        <p className="text-zinc-700 dark:text-zinc-300">
          {t('support.settings.owner')}: {settings.owner_connected ? <span className="font-semibold">{settings.owner_name}</span> : <span className="text-amber-600">{t('support.settings.ownerMissing')}</span>}
          {settings.owner_link && <> · <a className="text-brand-600" href={settings.owner_link} target="_blank" rel="noreferrer">{t('support.owner.connect')}</a></>}
        </p>
        <div>
          <p className="mb-2 text-zinc-500">{t('support.settings.replace')}</p>
          <TokenForm compact onSaved={(data) => { onChanged(data); onClose(); }} />
        </div>
        <div className="border-t border-zinc-200 pt-3 dark:border-zinc-800">
          <Button variant="danger" icon={Unplug} onClick={disconnect} loading={busy}>{t('support.settings.disconnect')}</Button>
        </div>
      </div>
    </Modal>
  );
}
